import React, { useEffect, useState } from 'react';
import { db } from "app/firebaseInit";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    TableContainer, Paper, Table, TableRow, TableHead, TableCell, TableBody, Typography,
    Backdrop, CircularProgress, Grid, Tabs, Tab
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConfirmationDialog, FileUploadComponent, PageLayout, SimpleCard } from 'app/components';
import { useSnackbar } from 'notistack';
import { archiveUploadsBySourceIdForOnboarding, uploadWithTrackingForContentOnboarding } from 'app/utils/uploadFile';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import styled from '@emotion/styled';
import config from 'config';
import { UPLOADS_COMPLETED_STATUS } from 'app/utils/constant';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const TextField = styled(TextValidator)(() => ({
    width: "100%",
    marginBottom: "16px"
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pl: 3, pt: 1, width: "100%" }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return { id: `vertical-tab-${index}`, 'aria-controls': `vertical-tabpanel-${index}` };
}

const OnBoardingScreen = () => {
    const [slides, setSlides] = useState([]);
    const [originalSlides, setOriginalSlides] = useState([]); // Orijinal listeyi saklamak için
    const [saveDialog, setSaveDialog] = useState(false);
    const [formData, setFormData] = useState({ title: { en: '', tr: '' }, summary: { en: '', tr: '' }, imageUrl: '', order: 0 });
    const [value, setValue] = useState(0);
    const [isEdit, setIsEdit] = useState(false);
    const [file, setFile] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [fileUrl, setFileUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isDragMode, setIsDragMode] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const collectionName = 'onBoarding';

    useEffect(() => { fetchSlides(); }, []);

    const fetchSlides = async () => {
        try {
            setLoading(true);
            const slidesCollection = collection(db, collectionName);
            const slidesQuery = query(slidesCollection, orderBy('order'));
            const slidesSnapshot = await getDocs(slidesQuery);
            const slidesList = slidesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSlides(slidesList);
        } catch (error) {
            console.error("Error fetchSlides: ", error);
        } finally { setLoading(false); }
    };

    const handleTabChange = (event, newValue) => { setValue(newValue); };
    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setErrors(prev => ({ ...prev, file: "" }));
            setFile(selectedFile);
            setFileUrl(URL.createObjectURL(selectedFile));
        } else { setErrors(prev => ({ ...prev, file: "error." })); }
    };

    const handleSubmit = async () => { if (validateForm()) await handleAddUpdate(); };

    const validateForm = () => {
        const newErrors = {};
        let firstInvalidTab = null;
        if (!formData.title.tr) { newErrors.title = { ...newErrors.title, tr: true }; if (firstInvalidTab === null) firstInvalidTab = 0; }
        if (!formData.summary.tr) { newErrors.summary = { ...newErrors.summary, tr: true }; if (firstInvalidTab === null) firstInvalidTab = 0; }
        if (!formData.title.en) { newErrors.title = { ...newErrors.title, en: true }; if (firstInvalidTab === null) firstInvalidTab = 1; }
        if (!formData.summary.en) { newErrors.summary = { ...newErrors.summary, en: true }; if (firstInvalidTab === null) firstInvalidTab = 1; }
        setErrors(newErrors);
        if (firstInvalidTab !== null) setValue(firstInvalidTab);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddUpdate = async () => {
        try {
            if (!fileUrl) { setErrors(prev => ({ ...prev, file: "error." })); return; }
            setLoading(true);
            let imagePath = null, iconUploadRef = null;
            if (file) {
                const { uploadRef, path } = await uploadWithTrackingForContentOnboarding(file, { sourceId: isEdit ? formData.id : null });
                imagePath = path; iconUploadRef = uploadRef;
            } else if (isEdit && formData.imageUrl) imagePath = formData.imageUrl;
            formData.imageUrl = imagePath;
            let slideId;
            if (!isEdit) {
                const slidesCollection = collection(db, collectionName);
                const slidesQuery = query(slidesCollection, orderBy("order", "desc"));
                const slidesSnapshot = await getDocs(slidesQuery);
                const maxOrder = slidesSnapshot.empty ? 0 : slidesSnapshot.docs[0].data().order || 0;
                formData.order = maxOrder + 1;
                const docRef = await addDoc(slidesCollection, formData);
                slideId = docRef.id;
            } else {
                slideId = formData.id;
                const slideRef = doc(db, collectionName, formData.id);
                await updateDoc(slideRef, {
                    title: formData.title,
                    summary: formData.summary,
                    imageUrl: formData.imageUrl,
                    order: formData.order
                });
            }
            if (isEdit && file) await archiveUploadsBySourceIdForOnboarding(slideId);
            if (iconUploadRef) await updateDoc(iconUploadRef, { status: UPLOADS_COMPLETED_STATUS, sourceId: slideId, updatedAt: serverTimestamp() });
            fetchSlides();
            enqueueSnackbar(isEdit ? "Onboarding updated successfully!" : "Onboarding added successfully!", { variant: "success" });
            handleCloseDialog();
        } catch (error) { console.error("Error saving onboarding: ", error); enqueueSnackbar("Failed to save onboarding!", { variant: "error" }); }
        finally { setLoading(false); }
    };

    const handleOpenDialog = (slide = { title: { en: '', tr: '' }, summary: { en: '', tr: '' }, imageUrl: '', order: '' }) => {
        setFormData(slide); setIsEdit(!!slide.id);
        if (slide.id) setFileUrl(`${config.IMGIX_BASE_URL}/${slide.imageUrl}?w=400&auto=format`);
        setSaveDialog(true);
    };
    const handleCloseDialog = () => { setSaveDialog(false); setFileUrl(""); setErrors({}); setValue(0); setFile(null); setFormData({ title: { en: '', tr: '' }, summary: { en: '', tr: '' }, imageUrl: '', order: 0 }); };
    const handleOpenDeleteDialog = (slide) => { setFormData(slide); setDeleteDialog(true); };
    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const slideRef = doc(db, collectionName, formData.id);
            await deleteDoc(slideRef);
            await archiveUploadsBySourceIdForOnboarding(formData.id);
            enqueueSnackbar("Onboarding deleted successfully!", { variant: "success" });
            fetchSlides();
        } catch (error) { console.error(error); enqueueSnackbar("Failed to delete onboarding!", { variant: "error" }); }
        finally { setLoading(false); setDeleteDialog(false); }
    };
    const handleCloseDeleteDialog = () => { setDeleteDialog(false); setFormData({ title: { en: '', tr: '' }, summary: { en: '', tr: '' }, imageUrl: '', order: 0 }); };
    const handleInputChange = (field, language) => (event) => { setFormData({ ...formData, [field]: language ? { ...formData[field], [language]: event.target.value } : event.target.value }); if (errors[field]?.[language]) setErrors(prev => ({ ...prev, [field]: { ...prev[field], [language]: false } })); };

    // 🔹 Drag & Drop işlemi
    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(slides);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setSlides(items);
    };

    const saveNewOrder = async () => {
        try {
            setLoading(true);
            const batch = slides.map((slide, index) => {
                const slideRef = doc(db, collectionName, slide.id);
                return updateDoc(slideRef, { order: index + 1 });
            });
            await Promise.all(batch);
            enqueueSnackbar("Order updated successfully!", { variant: "success" });
            setIsDragMode(false);
            fetchSlides();
        } catch (error) { console.error(error); enqueueSnackbar("Failed to update order!", { variant: "error" }); }
        finally { setLoading(false); }
    };

    return (
        <PageLayout routeSegments={[{ name: "Onboarding Screen" }]}>
            <SimpleCard title="Onboarding Screen">
                <Box width="100%" overflow="auto" sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Button
                                variant="outlined"
                                color={isDragMode ? "secondary" : "primary"}
                                onClick={() => {
                                    if (isDragMode) {
                                        // Cancel Sorting
                                        setSlides([...originalSlides]);
                                        setIsDragMode(false);
                                    } else {
                                        // Edit Order
                                        setOriginalSlides([...slides]);
                                        setIsDragMode(true);
                                    }
                                }}
                                sx={{ mr: 1 }}
                            >
                                {isDragMode ? "Cancel Sorting" : "Edit Order"}
                            </Button>
                            {isDragMode && (
                                <Button variant="contained" color="primary" onClick={saveNewOrder}>
                                    Save Order
                                </Button>
                            )}
                        </Box>
                        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>Add Page</Button>
                    </Box>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="slides" isDropDisabled={!isDragMode}>
                            {(provided) => (
                                <TableContainer component={Paper} ref={provided.innerRef} {...provided.droppableProps}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ width: 50 }}>Order</TableCell>
                                                <TableCell sx={{ width: 300 }}>Title</TableCell>
                                                <TableCell sx={{ flexGrow: 1 }}>Summary</TableCell>
                                                <TableCell sx={{ width: 120 }}>Image</TableCell>
                                                <TableCell sx={{ width: 100 }} align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {slides.map((slide, index) => (
                                                <Draggable key={slide.id} draggableId={slide.id} index={index} isDragDisabled={!isDragMode}>
                                                    {(provided, snapshot) => (
                                                        <TableRow ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} sx={{ backgroundColor: snapshot.isDragging ? "#f0f0f0" : "inherit" }}>
                                                            <TableCell>{index + 1}</TableCell>
                                                            <TableCell>
                                                                <Box display="flex" flexDirection="column">
                                                                    <Typography variant="body2" color="text.secondary">TR: {slide.title.tr}</Typography>
                                                                    <Typography variant="body1" color="text.primary">EN: {slide.title.en}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box display="flex" flexDirection="column">
                                                                    <Typography variant="body2" color="text.secondary">TR: {slide.summary.tr}</Typography>
                                                                    <Typography variant="body1" color="text.primary">EN: {slide.summary.en}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <img src={`${config.IMGIX_BASE_URL}/${slide.imageUrl}?w=400&auto=format`} alt={slide.title.en} style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <IconButton onClick={() => handleOpenDialog(slide)} color="primary"><EditIcon /></IconButton>
                                                                <IconButton onClick={() => handleOpenDeleteDialog(slide)} color="error"><DeleteIcon /></IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {/* Dialog ve Delete Confirmation */}
                    <Dialog open={saveDialog} onClose={(e) => e.stopPropagation()} maxWidth="md" fullWidth>
                        <DialogTitle>{isEdit ? "Update Page" : "Add New Page"}</DialogTitle>
                        <ValidatorForm onSubmit={handleSubmit}>
                            <DialogContent>
                                <Box sx={{ display: 'flex', height: 320 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={2}>
                                            <Tabs orientation="vertical" variant="scrollable" value={value} onChange={handleTabChange} sx={{ borderRight: 1, borderColor: 'divider' }}>
                                                <Tab label="TR" {...a11yProps(0)} />
                                                <Tab label="EN" {...a11yProps(1)} />
                                            </Tabs>
                                        </Grid>
                                        <Grid item xs={12} sm={10}>
                                            <TabPanel value={value} index={0}>
                                                <TextField label="Title (TR)" value={formData.title.tr} onChange={handleInputChange('title', 'tr')} validators={["required"]} error={!!errors.title?.tr} helperText={errors.title?.tr ? "This field is required" : ""} />
                                                <TextField label="Summary (TR)" value={formData.summary.tr} onChange={handleInputChange('summary', 'tr')} validators={["required"]} error={!!errors.summary?.tr} helperText={errors.summary?.tr ? "This field is required" : ""} />
                                            </TabPanel>
                                            <TabPanel value={value} index={1}>
                                                <TextField label="Title (EN)" value={formData.title.en} onChange={handleInputChange('title', 'en')} validators={["required"]} error={!!errors.title?.en} helperText={errors.title?.en ? "This field is required" : ""} />
                                                <TextField label="Summary (EN)" value={formData.summary.en} onChange={handleInputChange('summary', 'en')} validators={["required"]} error={!!errors.summary?.en} helperText={errors.summary?.en ? "This field is required" : ""} />
                                            </TabPanel>
                                            <Box mt={2}><FileUploadComponent type="image" name="Image" fileUrl={fileUrl} handleFileChange={handleFileChange} setFileUrl={setFileUrl} setFile={setFile} error={!!errors.file} helperText='Image is required' /></Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
                                <Button type="submit" color="primary">{isEdit ? "Update" : "Add"}</Button>
                                <Backdrop open={loading} sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }}>
                                    <CircularProgress color="inherit" />
                                </Backdrop>
                            </DialogActions>
                        </ValidatorForm>
                    </Dialog>
                    <ConfirmationDialog title="Confirm Delete" text='Are you sure you want to delete this item?' open={deleteDialog} onConfirmDialogClose={handleCloseDeleteDialog} onYesClick={handleConfirmDelete} />
                </Box>
            </SimpleCard>
        </PageLayout>
    );
};

export default OnBoardingScreen;
